import React, { useState, useEffect, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { loadStripe } from "@stripe/stripe-js";
import { Elements, CardElement, useStripe, useElements } from "@stripe/react-stripe-js";
import { AuthContext } from "../contexts/AuthContext.js";
import "./styles/Subscribe.css";

let stripePromise = null;

const getStripePromise = async () => {
  if (!stripePromise) {
    const resp = await fetch("/api/config");
    const data = await resp.json();
    if (data.stripe_publishable_key) {
      stripePromise = loadStripe(data.stripe_publishable_key);
    }
  }
  return stripePromise;
};

const formatPrice = (cents) => {
  return `$${(cents / 100).toFixed(2)}`;
};

const PlanCard = ({ plan, selected, onSelect }) => {
  const hasSpecial = plan.special_price_cents && plan.special_price_cents > 0;

  return (
    <div
      className={`plan-card ${selected ? "plan-card-selected" : ""}`}
      onClick={() => onSelect(plan)}
    >
      {hasSpecial && <div className="plan-badge">Special Offer</div>}
      <h3 className="plan-name">{plan.name}</h3>
      <div className="plan-pricing">
        {hasSpecial ? (
          <>
            <span className="plan-price-old">{formatPrice(plan.price_cents)}</span>
            <span className="plan-price">{formatPrice(plan.special_price_cents)}</span>
          </>
        ) : (
          <span className="plan-price">{formatPrice(plan.price_cents)}</span>
        )}
        <span className="plan-interval">/{plan.interval_label.toLowerCase()}</span>
      </div>
      {hasSpecial && plan.special_label && (
        <p className="plan-special-label">{plan.special_label}</p>
      )}
      <ul className="plan-features">
        <li>Unlimited article access</li>
        <li>Full content &amp; images</li>
        <li>All categories included</li>
      </ul>
    </div>
  );
};

const CheckoutForm = ({ selectedPlan, token }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) return;

    setLoading(true);
    setError(null);

    try {
      const { error: pmError, paymentMethod } = await stripe.createPaymentMethod({
        type: "card",
        card: elements.getElement(CardElement),
      });

      if (pmError) {
        setError(pmError.message);
        setLoading(false);
        return;
      }

      const resp = await fetch("/api/subscriptions/create-subscription", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          plan_id: selectedPlan.id,
          payment_method_id: paymentMethod.id,
        }),
      });

      const data = await resp.json();

      if (!resp.ok) {
        throw new Error(data.message || "Subscription failed");
      }

      if (data.client_secret) {
        const { error: confirmError } = await stripe.confirmCardPayment(data.client_secret);
        if (confirmError) {
          setError(confirmError.message);
          setLoading(false);
          return;
        }
      }

      setSuccess(true);
      setTimeout(() => {
        window.location.href = "/";
      }, 2000);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="checkout-success">
        <h3>Subscription Active!</h3>
        <p>Thank you for subscribing. You now have full access to all VyralNews content.</p>
        <p>Redirecting to homepage...</p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="checkout-form">
      <h3>Payment Details</h3>
      <p className="checkout-summary">
        {selectedPlan.name} — {formatPrice(
          selectedPlan.special_price_cents && selectedPlan.special_price_cents > 0
            ? selectedPlan.special_price_cents
            : selectedPlan.price_cents
        )}/{selectedPlan.interval_label.toLowerCase()}
      </p>
      <div className="card-element-container">
        <CardElement
          options={{
            style: {
              base: {
                fontSize: "16px",
                color: "#1e293b",
                fontFamily: "'Inter', sans-serif",
                "::placeholder": { color: "#94a3b8" },
              },
              invalid: { color: "#dc2626" },
            },
          }}
        />
      </div>
      {error && <div className="checkout-error">{error}</div>}
      <button type="submit" disabled={!stripe || loading} className="checkout-btn">
        {loading ? "Processing..." : "Subscribe Now"}
      </button>
      <p className="checkout-secure">Your payment information is securely processed by Stripe.</p>
    </form>
  );
};

const Subscribe = () => {
  const { user, token } = useContext(AuthContext);
  const navigate = useNavigate();
  const [plans, setPlans] = useState([]);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [stripeReady, setStripeReady] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!user) {
      navigate("/login", { replace: true });
      return;
    }
    if (user.role === "user" || user.role === "admin") {
      navigate("/", { replace: true });
      return;
    }

    const init = async () => {
      try {
        const [plansResp, sp] = await Promise.all([
          fetch("/api/subscription-plans"),
          getStripePromise()
        ]);
        const plansData = await plansResp.json();
        setPlans(plansData);
        setStripeReady(sp);
        if (plansData.length > 0) setSelectedPlan(plansData[0]);
      } catch (err) {
        setError("Failed to load subscription plans");
      }
    };
    init();
  }, [user, navigate]);

  if (!user) return null;

  return (
    <div className="subscribe-page">
      <div className="subscribe-container">
        <div className="subscribe-header">
          <h1>Choose Your Plan</h1>
          <p>Get unlimited access to all VyralNews articles and exclusive content.</p>
        </div>

        {error && <div className="subscribe-error">{error}</div>}

        <div className="plans-grid">
          {plans.map((plan) => (
            <PlanCard
              key={plan.id}
              plan={plan}
              selected={selectedPlan?.id === plan.id}
              onSelect={setSelectedPlan}
            />
          ))}
        </div>

        {selectedPlan && stripeReady && (
          <Elements stripe={stripeReady}>
            <CheckoutForm selectedPlan={selectedPlan} token={token} />
          </Elements>
        )}
      </div>
    </div>
  );
};

export default Subscribe;
